import type {
  IDocumentResponse,
  IDocumentUpload,
} from '~/interfaces/dto/compliance/documentVerificationInterfaces';
import type { IBaseResponse } from '~/interfaces/dto/base/request';
import { ESumSubAction } from '~/interfaces/dto/compliance/request';
import type {
  IGetNamesToVerify,
  ISumSubEvaluateResponse,
  ISumSubGenerateTokenResponse,
} from '~/interfaces/dto/compliance/response';

export const useComplianceService = () => {
  const { $fetch } = useNuxtApp();
  const { $trackEvent } = useNuxtApp();

  //documents
  const getDocuments = async () => {
    try {
      return await $fetch('v2/Compliance/uploaded-documents', {
        base: 'authApi',
        useAuthentication: true,
        useBrandId: true,
      });
    } catch (e) {
      console.error(e);
      $trackEvent('Account', 'DOC_VERIFICATION_ERROR', {
        message: e?.message || e?.cause?.message,
      });
    }
  };
  const uploadDocument = async (document: IDocumentUpload) => {
    try {
      return await $fetch<IDocumentResponse>('v2/Compliance/upload-document', {
        method: 'post',
        base: 'authApi',
        body: document,
        useAuthentication: true,
        useBrandId: true,
      });
    } catch (e) {
      console.error(e);
      $trackEvent('Account', 'DOC_VERIFICATION_ERROR', {
        message: e?.message || e?.cause?.message,
      });
    }
  };
  const deleteDocument = async (docId: string) => {
    try {
      return await $fetch<
        IBaseResponse & { data: { success: boolean; message: string } }
      >('v2/Compliance/delete-document', {
        method: 'POST',
        base: 'authApi',
        useAuthentication: true,
        useBrandId: true,
        query: { documentId: docId },
      });
    } catch (e) {
      console.error(e);
    }
  };

  // post registration
  const getPostRegNames = async () => {
    try {
      return await $fetch<IGetNamesToVerify>('v2/Compliance/names-to-verify', {
        base: 'base',
        useAuthentication: true,
        useBrandId: true,
        onResponse({ response }) {
          if (!!response._data && response?._data?.isSuccessful) {
            return response._data.data;
          } else {
            // TODO: Google analytics?
            console.error(response);
          }
        },
        onRequestError({ response }) {
          console.error(response);
        },
      });
    } catch (e) {
      console.error(e);
    }
  };
  const verifyPostRegNameSelection = async (selectedName: string) => {
    try {
      return await $fetch('v2/Compliance/verify-selected-name', {
        method: 'post',
        useAuthentication: true,
        query: { selectedName },
      });
    } catch (e) {
      console.error(e);
    }
  };

  //sum sub generates
  const sumSubGenerateTokenFica = async (
    action: ESumSubAction,
  ): Promise<ISumSubGenerateTokenResponse> => {
    return await $fetch<ISumSubGenerateTokenResponse>(
      '/v2/Compliance/fica-generate-token',
      {
        base: 'authApi',
        method: 'POST',
        body: { action },
        useAuthentication: true,
        useBrandId: true,
      },
    );
  };
  const sumSubGenerateToken = async (
    action: ESumSubAction,
  ): Promise<ISumSubGenerateTokenResponse> => {
    return await $fetch<ISumSubGenerateTokenResponse>(
      '/v2/Compliance/liveness-generate-token',
      {
        base: 'authApi',
        method: 'POST',
        body: { action },
        useAuthentication: true,
        useBrandId: true,
      },
    );
  };
  const sumSubGenerateTokenPasswordChange = async (
    mobileNumber: string,
    idNumber: string,
  ): Promise<ISumSubGenerateTokenResponse> => {
    const action = ESumSubAction.PasswordReset;
    return await $fetch<ISumSubGenerateTokenResponse>(
      '/v2/Compliance/liveness-generate-token',
      {
        base: 'authApi',
        method: 'POST',
        body: { mobileNumber, idNumber, action },
        useBrandId: true,
      },
    );
  };
  //sum sub evaluates
  const sumSubGenerateTokenFicaEvaluate = async (
    action: ESumSubAction,
    token: string,
  ): Promise<ISumSubEvaluateResponse> => {
    return await $fetch<ISumSubEvaluateResponse>(
      '/v2/Compliance/fica-evaluate-compliance-status',
      {
        base: 'authApi',
        method: 'POST',
        body: { action, token },
        useAuthentication: true,
        useBrandId: true,
      },
    );
  };
  const sumSubGenerateTokenEvaluate = async (
    action: ESumSubAction,
    token: string,
  ): Promise<ISumSubEvaluateResponse> => {
    return await $fetch<ISumSubEvaluateResponse>(
      '/v2/Compliance/liveness-evaluate-token',
      {
        base: 'authApi',
        method: 'POST',
        body: { action, token },
        useAuthentication: true,
        useBrandId: true,
      },
    );
  };
  const sumSubGenerateTokenPasswordChangeEvaluate = async (
    token: string,
  ): Promise<ISumSubEvaluateResponse> => {
    const action = ESumSubAction.PasswordReset;
    return await $fetch<ISumSubEvaluateResponse>(
      '/v2/Compliance/liveness-evaluate-token',
      {
        base: 'authApi',
        method: 'POST',
        body: { action, token },
        useBrandId: true,
      },
    );
  };

  return {
    getDocuments,
    uploadDocument,
    deleteDocument,
    getPostRegNames,
    verifyPostRegNameSelection,
    sumSubGenerateTokenFica,
    sumSubGenerateToken,
    sumSubGenerateTokenPasswordChange,
    sumSubGenerateTokenFicaEvaluate,
    sumSubGenerateTokenEvaluate,
    sumSubGenerateTokenPasswordChangeEvaluate,
  };
};
